export interface Vec3 { x: number; y: number; z: number }

export interface ShoulderCameraParams {
  playerX: number;
  playerZ: number;
  /** Radians; 0 = looking toward world +Z (matches OverShoulderCameraController's convention). */
  viewYaw: number;
  viewPitch: number;
  /** Continuous -1..1 during the shoulder-swap blend, not just the discrete ±1 target. */
  shoulderSide: number;
  shoulderOffsetM: number;
  shoulderHeightM: number;
  /** Already collision-adjusted — see DungeonInstanceScene.followLocalPlayer(). */
  boomLengthM: number;
}

/**
 * design/gdd/camera-and-controls.md — pure math for the fixed over-the-
 * shoulder rig, kept separate from DungeonInstanceScene (THREE.js/collision
 * concerns) so the trig itself is unit-testable without a renderer. `right`
 * is `forward × up`, matching OverShoulderCameraController.worldDirection's
 * already-established convention (at yaw=0, right = -X) — using the same
 * vector for both movement and the shoulder offset keeps "your right
 * shoulder" and "strafe right" pointing the same way.
 */
/**
 * Game Feel Polish Phase 5 — the `forward`/`right`/`anchor` intermediates
 * used to be object literals, so this allocated 6 short-lived objects per
 * call, once per frame, in a file sitting directly beside the one that
 * preallocates `THREE.Vector3` fields specifically to avoid that (see
 * `dungeon-instance-scene.ts`'s "reused every frame ... no-per-frame-
 * allocations" comment). They're plain scalars now. The returned `eye`/
 * `lookAt` objects stay — this function's whole point is being pure and
 * renderer-free so the trig is unit-testable, and threading out-params
 * through it would trade that away for two allocations a frame.
 */
export function computeShoulderCameraPose(p: ShoulderCameraParams): { eye: Vec3; lookAt: Vec3 } {
  const cosPitch = Math.cos(p.viewPitch);
  const forwardX = Math.sin(p.viewYaw) * cosPitch;
  const forwardY = Math.sin(p.viewPitch);
  const forwardZ = Math.cos(p.viewYaw) * cosPitch;
  // forward × (0,1,0) — y is always 0, so only the x/z components are needed.
  //
  // Derived from yaw alone rather than as (-forwardZ, 0, forwardX): that cross
  // product has magnitude |cos(pitch)|, not 1, so the shoulder offset used to
  // quietly shrink as the player pitched the view — up to ~17% at the pitch
  // limits, sliding the camera back toward directly-behind exactly when
  // looking up or down. Using cos/sin of yaw keeps it a true unit vector, so
  // the offset means the same thing at every pitch.
  const rightX = -Math.cos(p.viewYaw);
  const rightZ = Math.sin(p.viewYaw);

  const anchorX = p.playerX + rightX * p.shoulderOffsetM * p.shoulderSide;
  const anchorY = p.shoulderHeightM;
  const anchorZ = p.playerZ + rightZ * p.shoulderOffsetM * p.shoulderSide;

  const eye: Vec3 = {
    x: anchorX - forwardX * p.boomLengthM,
    y: anchorY - forwardY * p.boomLengthM,
    z: anchorZ - forwardZ * p.boomLengthM,
  };

  const LOOK_AHEAD_M = 10;
  const lookAt: Vec3 = {
    x: eye.x + forwardX * LOOK_AHEAD_M,
    y: eye.y + forwardY * LOOK_AHEAD_M,
    z: eye.z + forwardZ * LOOK_AHEAD_M,
  };

  return { eye, lookAt };
}
